<template lang="pug">
  .image-section.container.mx-auto.px-12(
    ref="container"
    :class="[spacingTop, spacingBottom, fit]"
  )
    .image(v-if="image")
      figure(:style="{ width: `${image.width}px` }")
        img(
          v-lazy="image"
          :key="content.id"
          :title="title || subTitle"
          :style="{ width: `${image.width}px`, height: `${image.height}px` }"
          rel="noopener"
        )
      figcaption(v-if="title || subTitle")
        p(v-if="title") {{ title }}
        small(v-if="subTitle") {{ subTitle }}
</template>

<script>
import { mapGetters } from 'vuex';

import { get, debounce, trim } from '@/helpers';

export default {
    name: 'ImageSection',
    data() {
        return {
            maxHeight: undefined,
            maxWidth: undefined
        };
    },
    props: {
        section: {
            type: Object,
            required: true
        }
    },
    computed: {
        contents() {
            const contents = get(this.section, 'contents') || [];
            return contents.sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
        },

        spacingTop() {
            let spacingTop = 'spacingTopStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingTop');
                if (setting && setting.value !== undefined) spacingTop = setting.value;
            }
            return spacingTop;
        },

        spacingBottom() {
            let spacingBottom = 'spacingBottomStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingBottom');
                if (setting && setting.value !== undefined) spacingBottom = setting.value;
            }
            return spacingBottom;
        },

        content() {
            return get(this.contents, 0);
        },
        title() {
            return get(this.content, 'title');
        },
        subTitle() {
            return get(this.content, 'sub_title');
        },
        fit() {
            let fit = 'contain';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'fit');
                if (setting && setting.value !== undefined) fit = setting.value;
            }

            return fit;
        },
        image() {
            const image = get(this.content, 'images.0');
            if (!image) return false;

            return this.computeImageObject(image);
        },
        ...mapGetters({
            tailwind: 'tailwind'
        })
    },
    methods: {
        computeImageObject(image) {
            if (!this.maxWidth || !this.maxHeight) return false;

            // get the crop parameters

            let fit = get(image, 'pivot.fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';
            const parameters = {
                ...get(fit.match(/x=(?<fx>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/y=(?<fy>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/z=(?<fz>[1-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups')
            };

            let { w, h, fx, fy, fz } = parameters;
            // let { fx, fy, fz } = parameters;

            if (!fx) fx = 0.5;
            if (!fy) fy = 0.5;
            if (!fz) fz = 1;

            let width, height, aspectRatio;

            // get the actual image dimensions

            width = parseFloat(w) || image.width / fz;
            height = parseFloat(h) || image.height / fz;
            aspectRatio = width / height;

            const availableAspectRatio = this.maxWidth / this.maxHeight;

            // define the crop parameters
            // based on available space and user settings

            // if image fit is "cover"
            // render the image crop (full width and height) as background

            const breakpoint = parseInt(this.tailwind.theme.screens.lg);

            if (this.fit === 'cover') {
                if (window.innerWidth < breakpoint || availableAspectRatio > aspectRatio) {
                    // scale by width
                    width = Math.min(this.maxWidth, image.width);
                    height = width / aspectRatio;

                    if (height > this.maxHeight) {
                        // calculate correct zoom by heigh / maxHeight ratio

                        // fz = fz * height / this.maxHeight
                        height = Math.min(this.maxHeight, width / aspectRatio);
                        width = height * availableAspectRatio;
                    }
                } else {
                    // scale by height
                    height = Math.min(this.maxHeight, image.height);
                    width = height * aspectRatio;

                    if (width > this.maxWidth) {
                        // calculate correct zoom by width / maxWidth ratio

                        fz = (fz * width) / this.maxWidth;
                        width = Math.min(this.maxWidth, height * aspectRatio);
                        height = width / availableAspectRatio;
                    }
                }
            }

            // if image fit is "contain"
            // render the image on the left half
            // and take as much space as possible

            if (this.fit === 'contain') {
                if (window.innerWidth < breakpoint || availableAspectRatio <= aspectRatio) {
                    // scale by width
                    width = Math.min(this.maxWidth, image.width);
                    height = width / aspectRatio;
                } else {
                    // scale by height
                    height = Math.min(this.maxHeight, image.height);
                    width = height * aspectRatio;
                }
            }

            fit = `fit=crop&crop=focalpoint&x=${fx}&y=${fy}&z=${fz}&w=${width}&h=${height}`;

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            return {
                src: `${storageRoute}/images/${image.id}/${image.name}?${fit}`,
                error: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=blur`,
                width,
                height,
                aspectRatio
            };
        }
    },
    mounted() {
        const calculateDimensions = () => {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container);

                this.maxHeight = this.$refs.container.clientHeight;
                this.maxHeight -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

                this.maxWidth = this.$refs.container.clientWidth;
                this.maxWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
            }
        };

        window.addEventListener('resize', debounce(calculateDimensions, 100));
        calculateDimensions();
    }
};
</script>

<style lang="sass" nolang="scss" scoped>
@import "~@/assets/sass/variables"

.image-section
  display: flex
  justify-content: center
  min-height: 40px
  @screen lg
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    min-height: 500px
    &.cover
      padding: 0
      .image
        height: 100vh
        width: 100%
        figure
          height: 100vh !important
          width: 100% !important
          position: relative
          img
            height: 100vh !important
            width: 100% !important
            border-radius: 0
            object-fit: cover
            display: block
        figcaption
          position: absolute
          top: 0
          right: 0
          padding: 10px
          text-align: right
  &.contain
    overflow: hidden
    .image
      figure
        img
          &[lazy=loading]
            // box-shadow: none
          &[lazy=loaded]
            // max-height: 500px
            height: auto !important
            width: 100% !important
  .image
    transition: opacity .3s ease
    width: 100%
    height: 100%
    display: flex
    justify-content: center
    align-items: center
    figure
      max-width: 100vw
      display: block
      margin-bottom: 2px
      img
        width: initial
        height: auto
        display: inline-block
        object-fit: cover
        background-color: $skeletonColor
    figcaption
      p
        margin-bottom: 0
        font-size: .8rem
      small
        font-size: .65rem
        display: block
</style>
